// Mock client data for coach interface
import type { Client } from '@/types'

export const mockClients: Client[] = [
  {
    id: 'client-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phoneNumber: '+1-555-0123',
    profilePhoto: undefined,
    dateOfBirth: new Date('1990-03-15'),
    gender: 'female',
    
    // Coaching relationship
    coachId: 'coach-001',
    assignedDate: new Date('2024-01-10'),
    status: 'active',
    
    // Goals and targets
    goals: [
      {
        id: 'goal-001',
        type: 'strength',
        title: 'Increase Squat Strength',
        description: 'Work up to squatting bodyweight for 5 reps',
        target: {
          type: 'numeric',
          value: 140,
          unit: 'lbs',
          baseline: 115
        },
        deadline: new Date('2024-04-15'),
        priority: 'high',
        status: 'active',
        progress: 65,
        milestones: [
          {
            id: 'milestone-001',
            title: '125 lbs for 5 reps',
            targetValue: 125,
            currentValue: 125,
            unit: 'lbs',
            achieved: true,
            achievedDate: new Date('2024-01-28')
          },
          {
            id: 'milestone-002',
            title: '135 lbs for 5 reps',
            targetValue: 135,
            currentValue: 130,
            unit: 'lbs',
            achieved: false
          }
        ],
        createdAt: new Date('2024-01-10')
      },
      {
        id: 'goal-002',
        type: 'health',
        title: 'Improve Energy Levels',
        description: 'Feel more energetic throughout the day',
        target: {
          type: 'range',
          value: [7, 9],
          unit: 'energy-scale'
        },
        priority: 'medium',
        status: 'active',
        progress: 40,
        milestones: [],
        createdAt: new Date('2024-01-10')
      }
    ],
    
    // Current program
    currentProgram: {
      id: 'prog-001',
      name: 'Program: M_H_3_12',
      description: 'Male | Hypertrophy | 3 d/w | 12 sets weekly',
      clientId: 'client-001',
      coachId: 'coach-001',
      duration: 12,
      workoutsPerWeek: 3,
      startDate: new Date('2024-01-15'),
      endDate: new Date('2024-04-08'),
      difficulty: 'intermediate',
      focus: [{ type: 'strength', percentage: 70 }],
      equipment: ['barbell', 'bodyweight'],
      weeks: [],
      status: 'active',
      progress: {
        totalWorkouts: 36,
        completedWorkouts: 8,
        completionRate: 22,
        averageRating: 4.2,
        strengthGains: [],
        measurementChanges: [],
        goalProgress: []
      },
      templateId: undefined,
      isTemplate: false,
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date('2024-01-15')
    },
    
    // Measurements and tracking
    measurements: [
      {
        id: 'measurement-001',
        clientId: 'client-001',
        type: 'weight',
        value: 140,
        unit: 'lbs',
        date: new Date('2024-01-15'),
        takenBy: 'client'
      },
      {
        id: 'measurement-002',
        clientId: 'client-001',
        type: 'body-fat',
        value: 22,
        unit: '%',
        date: new Date('2024-01-15'),
        takenBy: 'coach'
      }
    ],
    
    progressPhotos: [
      {
        id: 'photo-001',
        clientId: 'client-001',
        imageUrl: '/mock-progress-photos/sarah-front-jan15.jpg',
        angle: 'front',
        date: new Date('2024-01-15'),
        tags: ['baseline', 'start-of-program']
      }
    ],
    
    // Communication and activity
    lastActivity: new Date('2024-01-20'),
    responseRate: 95,
    preferredContactMethod: 'app',
    
    // Quick stats for coach dashboard
    quickStats: {
      weeklyCompliance: 85,
      progressTrend: 'improving',
      engagementLevel: 'high',
      daysWithCoach: 45,
      currentStreak: 7,
      totalWorkouts: 8,
      averageRating: 4.2
    },
    
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-20')
  },

  {
    id: 'client-002',
    name: 'Mike Chen',
    email: 'mike.chen@email.com',
    phoneNumber: '+1-555-0124',
    profilePhoto: undefined,
    dateOfBirth: new Date('1985-08-22'),
    gender: 'male',
    
    coachId: 'coach-001',
    assignedDate: new Date('2024-01-05'),
    status: 'active',
    
    goals: [
      {
        id: 'goal-003',
        type: 'weight-loss',
        title: 'Lose 15 pounds',
        description: 'Healthy weight loss through strength training and nutrition',
        target: {
          type: 'numeric',
          value: 165,
          unit: 'lbs',
          baseline: 180
        },
        deadline: new Date('2024-06-01'),
        priority: 'high',
        status: 'active',
        progress: 50,
        milestones: [
          {
            id: 'milestone-003',
            title: 'First 5 pounds lost',
            targetValue: 175,
            currentValue: 173,
            unit: 'lbs',
            achieved: true,
            achievedDate: new Date('2024-01-25')
          }
        ],
        createdAt: new Date('2024-01-05')
      }
    ],
    
    currentProgram: {
      id: 'prog-002',
      name: 'Beginner Strength Program',
      description: 'Foundation building program for new lifters',
      clientId: 'client-002',
      coachId: 'coach-001',
      duration: 8,
      workoutsPerWeek: 3,
      startDate: new Date('2024-01-08'),
      difficulty: 'beginner',
      focus: [{ type: 'strength', percentage: 70 }],
      equipment: ['bodyweight', 'dumbbells'],
      weeks: [],
      status: 'active',
      progress: {
        totalWorkouts: 24,
        completedWorkouts: 18,
        completionRate: 75,
        averageRating: 4.6,
        strengthGains: [],
        measurementChanges: [],
        goalProgress: []
      },
      templateId: undefined,
      isTemplate: false,
      createdAt: new Date('2024-01-05'),
      updatedAt: new Date('2024-01-20')
    },
    
    measurements: [
      {
        id: 'measurement-003',
        clientId: 'client-002',
        type: 'weight',
        value: 173,
        unit: 'lbs',
        date: new Date('2024-01-20'),
        takenBy: 'client'
      }
    ],
    
    progressPhotos: [],
    
    lastActivity: new Date('2024-01-19'),
    responseRate: 78,
    preferredContactMethod: 'email',
    
    quickStats: {
      weeklyCompliance: 72,
      progressTrend: 'stable',
      engagementLevel: 'medium',
      daysWithCoach: 30,
      currentStreak: 3,
      totalWorkouts: 18,
      averageRating: 4.6
    },
    
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-19')
  },

  {
    id: 'client-003',
    name: 'Emma Wilson',
    email: 'emma.wilson@email.com',
    phoneNumber: '+1-555-0125',
    profilePhoto: undefined,
    dateOfBirth: new Date('1992-11-08'),
    gender: 'female',
    
    coachId: 'coach-001',
    assignedDate: new Date('2024-01-22'),
    status: 'onboarding',
    
    goals: [
      {
        id: 'goal-004',
        type: 'endurance',
        title: 'Run 5K without stopping',
        description: 'Build up cardiovascular endurance to complete a 5K race',
        target: {
          type: 'numeric',
          value: 5,
          unit: 'km'
        },
        deadline: new Date('2024-05-01'),
        priority: 'high',
        status: 'active',
        progress: 20,
        milestones: [
          {
            id: 'milestone-004',
            title: 'Run 1K without stopping',
            targetValue: 1,
            currentValue: 0.8,
            unit: 'km',
            achieved: false
          }
        ],
        createdAt: new Date('2024-01-22')
      }
    ],
    
    currentProgram: {
      id: 'prog-003',
      name: 'Beginner Fitness',
      description: 'Introduction to fitness fundamentals',
      clientId: 'client-003',
      coachId: 'coach-001',
      duration: 6,
      workoutsPerWeek: 3,
      startDate: new Date('2024-01-22'),
      difficulty: 'beginner',
      focus: [{ type: 'cardio', percentage: 60 }],
      equipment: ['bodyweight'],
      weeks: [],
      status: 'active',
      progress: {
        totalWorkouts: 18,
        completedWorkouts: 2,
        completionRate: 11,
        averageRating: 4.5,
        strengthGains: [],
        measurementChanges: [],
        goalProgress: []
      },
      templateId: undefined,
      isTemplate: false,
      createdAt: new Date('2024-01-20'),
      updatedAt: new Date('2024-01-25')
    },
    
    measurements: [
      {
        id: 'measurement-004',
        clientId: 'client-003',
        type: 'weight',
        value: 125,
        unit: 'lbs',
        date: new Date('2024-01-22'),
        takenBy: 'client'
      }
    ],
    
    progressPhotos: [],
    
    lastActivity: new Date('2024-01-25'),
    responseRate: 100,
    preferredContactMethod: 'app',
    
    quickStats: {
      weeklyCompliance: 95,
      progressTrend: 'improving',
      engagementLevel: 'high',
      daysWithCoach: 7,
      currentStreak: 2,
      totalWorkouts: 2,
      averageRating: 4.5
    },
    
    createdAt: new Date('2024-01-22'),
    updatedAt: new Date('2024-01-25')
  },

  {
    id: 'client-004',
    name: 'Alex Turner',
    email: 'alex.turner@email.com',
    phoneNumber: '+1-555-0126',
    profilePhoto: undefined,
    dateOfBirth: new Date('1988-06-14'),
    gender: 'male',
    
    coachId: 'coach-001',
    assignedDate: new Date('2023-11-15'),
    status: 'inactive',
    
    goals: [],
    
    measurements: [],
    progressPhotos: [],
    
    lastActivity: new Date('2024-01-05'),
    responseRate: 45,
    preferredContactMethod: 'email',
    
    quickStats: {
      weeklyCompliance: 25,
      progressTrend: 'declining',
      engagementLevel: 'low',
      daysWithCoach: 67,
      currentStreak: 0,
      totalWorkouts: 12,
      averageRating: 3.2
    },
    
    createdAt: new Date('2023-11-15'),
    updatedAt: new Date('2024-01-05')
  }
]

// Helper functions for client data
export const getClientById = (id: string): Client | undefined => {
  return mockClients.find(client => client.id === id)
}

export const getActiveClients = (): Client[] => {
  return mockClients.filter(client => client.status === 'active')
}

export const getClientsByStatus = (status: Client['status']): Client[] => {
  return mockClients.filter(client => client.status === status)
}

export const getClientsNeedingAttention = (): Client[] => {
  return mockClients.filter(client => 
    client.quickStats.weeklyCompliance < 60 || 
    client.quickStats.progressTrend === 'declining' ||
    client.quickStats.engagementLevel === 'low'
  )
}